import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import type { Product } from "@/lib/api";

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  clothes: "i-clothes",
  shoes: "i-shoes",
  bags: "i-bag",
  nails: "i-nails",
};

const TRENDING = [
  "Satin Dress",
  "Metallic Heels",
  "Clutch Bag",
  "Cat Eye Nails",
  "New Arrivals",
];

export default function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { products: allProducts } = useProducts();

  // autofocus on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 80);
      setQuery("");
      setActiveIndex(-1);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const results = useMemo<Product[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.fabricLabel.toLowerCase().includes(q) ||
        p.shortDescription.toLowerCase().includes(q) ||
        CATEGORY_LABELS[p.category]?.toLowerCase().includes(q) ||
        p.occasions.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  // group results by category
  const grouped = useMemo(() => {
    const map: Record<string, Product[]> = {};
    results.forEach((p) => {
      if (!map[p.category]) map[p.category] = [];
      map[p.category].push(p);
    });
    return map;
  }, [results]);

  const flatList = results;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && flatList[activeIndex]) {
        goToProduct(flatList[activeIndex]);
      }
    }
  };

  const goToProduct = (p: Product) => {
    onClose();
    navigate(`/products/${p.slug}`);
  };

  const goToTrending = (term: string) => {
    setQuery(term);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-midnight/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full bg-moonlight border-b border-pearl/40 pt-6 pb-8 px-6 md:px-12 lg:px-24 shadow-2xl"
        style={{ animation: "searchSlideDown 0.25s ease" }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-6 md:right-12 w-9 h-9 flex items-center justify-center text-midnight/40 hover:text-midnight transition-colors cursor-pointer"
          aria-label="Close search"
        >
          <i className="ri-close-line text-2xl" />
        </button>

        {/* Search Input */}
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 border-b-2 border-midnight pb-3">
            <div className="w-6 h-6 flex items-center justify-center shrink-0">
              <i className="ri-search-line text-xl text-midnight/50" />
            </div>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search dresses, bags, heels, nails..."
              className="flex-1 bg-transparent text-lg md:text-2xl text-midnight placeholder-midnight/25 focus:outline-none"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
              aria-label="Search products"
            />
            {query && (
              <button
                onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                className="w-6 h-6 flex items-center justify-center text-midnight/30 hover:text-midnight transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-circle-line text-lg" />
              </button>
            )}
          </div>

          {/* Trending — shown when no query */}
          {!query && (
            <div className="mt-6">
              <p className="text-[10px] tracking-widest uppercase text-midnight/35 mb-3">
                Trending Searches
              </p>
              <div className="flex flex-wrap gap-2">
                {TRENDING.map((term) => (
                  <button
                    key={term}
                    onClick={() => goToTrending(term)}
                    className="text-xs border border-pearl/60 text-midnight/55 px-3.5 py-1.5 hover:border-midnight hover:text-midnight transition-all cursor-pointer whitespace-nowrap"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {query && results.length === 0 && (
            <div className="mt-8 text-center">
              <p className="text-sm text-midnight/40">
                No results for &ldquo;<span className="text-midnight font-medium">{query}</span>&rdquo;
              </p>
              <p className="text-xs text-midnight/30 mt-1">Try searching for dresses, bags, or nail kits</p>
            </div>
          )}

          {/* Result count */}
          {query && results.length > 0 && (
            <p className="text-[10px] tracking-widest uppercase text-midnight/30 mt-5 mb-1">
              {results.length} result{results.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      {/* Results area — scrollable */}
      {query && results.length > 0 && (
        <div className="relative z-10 flex-1 overflow-y-auto bg-moonlight/97 backdrop-blur-sm">
          <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 py-6 pb-16 space-y-8">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                {/* Category header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] tracking-widest uppercase text-midnight/40">
                    {CATEGORY_LABELS[cat] ?? cat}
                  </span>
                  <span className="text-[10px] text-midnight/25">—  {items.length} item{items.length !== 1 ? "s" : ""}</span>
                </div>

                {/* Product rows */}
                <div className="space-y-1">
                  {items.map((p) => {
                    const flatIdx = flatList.indexOf(p);
                    const isActive = flatIdx === activeIndex;
                    return (
                      <button
                        key={p.id}
                        onClick={() => goToProduct(p)}
                        onMouseEnter={() => setActiveIndex(flatIdx)}
                        className={`w-full flex items-center gap-4 px-3 py-3 transition-all cursor-pointer text-left group ${
                          isActive ? "bg-pearl/40" : "hover:bg-pearl/20"
                        }`}
                      >
                        {/* Thumbnail */}
                        <div className="w-14 h-14 shrink-0 overflow-hidden bg-pearl/30">
                          <img
                            src={p.images[0]}
                            alt={p.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-midnight font-medium truncate"
                              style={{ fontFamily: "'Cormorant Garamond', serif" }}
                            >
                              {p.name}
                            </span>
                            {p.isNew && (
                              <span className="text-[9px] tracking-wider uppercase bg-midnight text-moonlight px-1.5 py-0.5 shrink-0">
                                New
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-midnight/45 mt-0.5 truncate">
                            {p.shortDescription}
                          </p>
                          <p className="text-[11px] text-midnight/40 mt-0.5">
                            {p.fabricLabel}
                          </p>
                        </div>

                        {/* Price + arrow */}
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <span className="text-sm font-medium text-midnight">${p.price}</span>
                            {p.originalPrice && (
                              <span className="text-[11px] text-midnight/30 line-through ml-1.5">${p.originalPrice}</span>
                            )}
                          </div>
                          <div className="w-6 h-6 flex items-center justify-center text-midnight/20 group-hover:text-midnight/60 transition-colors">
                            <i className="ri-arrow-right-s-line text-lg" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes searchSlideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
